import { Model } from '@lernetz/ts-lib';
import { Signal } from '@lernetz/ts-lib/src/Signal';
import { Repeat } from '@lernetz/ts-lib/src/components/Repeat';
import { View, ViewConfig } from '@lernetz/ts-lib/src/view/View';

export class DropDownView<TItem = any> extends View<DropDown<TItem>> {

    private closeHandler:(event) => void;

    constructor( config?:ViewConfig<DropDown> ) {
        super( { data:new DropDown(), ...config } );

        // handler to close dropdown from everywhere
        this.closeHandler = ( event ) => {
            this.data.open = false;
        }

        this.registry.add( 'Repeat', () => new Repeat() );
    }

    init() {
        // swallow click event
        this.node.addEventListener( 'click', ( event ) => {
            event.stopImmediatePropagation();
        });

        // listen on list open
        this.data.propertyChange.filter( ( change ) => change.name == 'open' && this.data.open ).subscribe( this.onOpen.bind( this ) );

        // listen on list close
        this.data.propertyChange.filter( ( change ) => change.name == 'open' && !this.data.open ).subscribe( this.onClose.bind( this ) );
    }

    onOpen() {
        document.addEventListener( 'click', this.closeHandler );
    }

    onClose() {
        document.removeEventListener( 'click', this.closeHandler );
    }
}

/**
 * Specialized `View` class that auto-closes the `DropDown` when it loses focus.
 *
 * This requires a template where the drop-down can receive the input focus.
 * Typically, you'll want to make the toggle `.button` focusable, as well as every
 * `.list .item` &ndash; e.g. by means of `tabindex="0"`.
 */
export class FocusableDropDownView<TItem = any> extends View<DropDown<TItem>> {

    constructor( config?:ViewConfig<DropDown> ) {
        super( { data: config?.data ?? new DropDown<unknown>(), ...config } );

        this.registry.add( 'Repeat', () => new Repeat() );
    }

    init() {
        this.node.addEventListener( 'focusout', () => this.data.open &&= this.node.matches( ':focus-within' ) );
    }
}

/**
 * Specialized `FocusableDropDownView` class that adds keyboard support.
 */
export class AccessibleDropDownView<TItem = any> extends FocusableDropDownView<DropDown<TItem>> {

    init() {
        super.init();

        this.node.addEventListener( 'keydown', ( e: KeyboardEvent ) => {
            if( !this.data.disabled && this.onKey( e, e.altKey || e.ctrlKey || e.metaKey || e.shiftKey ) ) {
                e.stopPropagation();
                e.preventDefault();
            }
        } );
    }

    protected onKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        switch( e.key ) {
            case 'ArrowDown': return this.onArrowDownKey( e, hasAnyModifierKey );
            case 'ArrowUp':   return this.onArrowUpKey  ( e, hasAnyModifierKey );
            case ' ':         return this.onSpaceKey    ( e, hasAnyModifierKey );
            case 'Enter':     return this.onEnterKey    ( e, hasAnyModifierKey );
        }
    }

    protected onArrowDownKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        if( hasAnyModifierKey ) return;

        const index = 1 + ( this.data.items?.indexOf( this.data.selection ) ?? -1 );
        if( index < this.data.items?.length ?? 0 ) {
            this.data.selection = this.data.items[ index ];
        }
        return true;
    }

    protected onArrowUpKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        if( hasAnyModifierKey ) return;

        const index = -1 + ( this.data.items?.indexOf( this.data.selection ) ?? -1 );
        if( index >= 0 ) {
            this.data.selection = this.data.items[ index ];
        }
        return true;
    }

    protected onEnterKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        return this.onEnterOrSpaceKey( e, hasAnyModifierKey );
    }

    protected onSpaceKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        return this.onEnterOrSpaceKey( e, hasAnyModifierKey );
    }

    private onEnterOrSpaceKey( e: KeyboardEvent, hasAnyModifierKey: boolean ): boolean {
        if( hasAnyModifierKey ) return;

        const listFocused = this.node.querySelector( '.list' )?.matches( ':focus-within' );
        if( listFocused ) {
            const focusedElement = e.target;
            if( focusedElement instanceof HTMLElement ) {
                focusedElement.click();
                const button = this.node.querySelector( '.button' );
                if( button instanceof HTMLElement ) {
                    button.focus();
                }
                this.data.open = false;
                return true;
            }
        }
        else {
            this.data.open = !this.data.open;
            return true;
        }
    }
}

export interface DropDownConfig<TItem = any> {
    disabled?: boolean;
    items?: TItem[];
    open?: boolean;
    selection?: TItem;
}

export class DropDown<TItem = any> extends Model {

    public disabled: boolean;
    public items: TItem[];
    public open: boolean;
    public selection?: TItem;

    constructor( config?: DropDownConfig<TItem> ) {
        super( { disabled: false, open: false, items: [], selection: null, ...config } );
    }

    select( item: TItem ) {
        if( this.disabled ) return;
        this.selection = item;
        this.open = false;
    }

    getIndex( item ) {
        return this.items.indexOf( item );
    }
}
